/**
 * Majhya Kolhapursathi — Main Script
 * Handles navigation, mobile drawer, scroll states, accessible interactions,
 * hero entrance sequence, and GSAP physical micro-interactions
 */

import { initHeroAnimation } from './utils/heroAnimation.js';
import { initMenu } from './components/menu.js';
import { initHighlightsAnimation } from './utils/highlightsAnimation.js';
import { initHallmarksDeck } from './utils/hallmarksDeck.js';
import { initCardAnimations } from './utils/animations.js';

document.addEventListener('DOMContentLoaded', () => {
  // 1. Critical above-the-fold interactions (Immediate)
  initNavigation();
  initHeroAnimation();
  initSmartMobileBar();

  // 2. Defer below-the-fold components and heavy animation observers until the main thread is idle
  const deferTask = window.requestIdleCallback || ((cb) => setTimeout(cb, 60));
  deferTask(() => {
    initHighlightsAnimation();
    initHallmarksDeck();
    initMenu('#menu-container');
    initCardAnimations();
  });
});

function initNavigation() {
  const header = document.querySelector('.site-header');
  const toggleBtn = document.querySelector('.mobile-menu-toggle');
  const drawer = document.querySelector('.mobile-nav-drawer');
  const navLinks = document.querySelectorAll('.nav-link, .mobile-nav-link');

  // Sticky header scroll elevation
  const handleScroll = () => {
    if (window.scrollY > 20) {
      header?.classList.add('scrolled');
    } else {
      header?.classList.remove('scrolled');
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll(); // Initial check

  // Mobile drawer toggle
  if (toggleBtn && drawer) {
    const toggleDrawer = (isOpen) => {
      const state = typeof isOpen === 'boolean' ? isOpen : !drawer.classList.contains('open');
      drawer.classList.toggle('open', state);
      toggleBtn.setAttribute('aria-expanded', String(state));
      document.body.style.overflow = state ? 'hidden' : '';
    };

    toggleBtn.addEventListener('click', () => toggleDrawer());

    // Close on navigation link click
    navLinks.forEach((link) => {
      link.addEventListener('click', () => {
        if (drawer.classList.contains('open')) {
          toggleDrawer(false);
        }
      });
    });

    // Close on Escape key
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('open')) {
        toggleDrawer(false);
        toggleBtn.focus();
      }
    });
  }
}


function initSmartMobileBar() {
  const mobileBar = document.querySelector('.mobile-bottom-bar');
  if (!mobileBar) return;

  let lastScrollY = window.scrollY;
  let ticking = false;

  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        if (window.innerWidth <= 859) {
          // Hide when scrolling down past 320px with sufficient velocity
          if (currentScrollY > 320 && currentScrollY > lastScrollY + 10) {
            mobileBar.classList.add('mobile-bar-hidden');
          } else if (currentScrollY < lastScrollY - 6 || currentScrollY <= 150) {
            mobileBar.classList.remove('mobile-bar-hidden');
          }
        } else {
          mobileBar.classList.remove('mobile-bar-hidden');
        }
        lastScrollY = currentScrollY;
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });
}
